
import React from "react";

// ─────────────────────────────────────────────────────────────────
// STYLE SYSTEM
// ─────────────────────────────────────────────────────────────────
const S = {
  wrapper:      { marginTop:'10px', marginLeft:'0px', border:'3px', padding:'40px', borderStyle:'solid', borderColor:'grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1:           { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2:           { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p:            { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox:      { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem:  { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table:        { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th:           { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE:          { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO:          { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL:          { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code:         { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap:        { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle:       { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption:     { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Definition / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// Shared arrow marker factory — unique IDs prevent cross-diagram conflicts
function ArrowMarker({ id, color }) {
  return (
    <marker id={id} markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto">
      <polygon points="0 0,7 3.5,0 7" fill={color}/>
    </marker>
  );
}

function Arrow({ x1, y1, x2, y2, color='#2E75B6', width=2.5, dash='', markerId }) {
  return (
    <line x1={x1} y1={y1} x2={x2} y2={y2}
      stroke={color} strokeWidth={width} strokeDasharray={dash}
      markerEnd={`url(#${markerId})`}/>
  );
}

// ─────────────────────────────────────────────────────────────────
// HELPERS — transform a 2D point by a 2×2 matrix
// ─────────────────────────────────────────────────────────────────
function applyMat(m, [x, y]) {
  return [m[0]*x + m[1]*y, m[2]*x + m[3]*y];
}

// Convert math coords → SVG coords given an origin and scale
function toSVG(ox, oy, sc, [x, y]) {
  return [ox + x * sc, oy - y * sc];
}

// Draw a simple 2D grid with axes in a bounding box
function Grid({ ox, oy, sc, xRange=[-2,2], yRange=[-2,2], clip }) {
  const [xMin, xMax] = xRange;
  const [yMin, yMax] = yRange;
  const ticks = [];
  for (let v = Math.ceil(xMin); v <= Math.floor(xMax); v++) ticks.push(v);
  const ticksY = [];
  for (let v = Math.ceil(yMin); v <= Math.floor(yMax); v++) ticksY.push(v);

  return (
    <g clipPath={clip ? `url(#${clip})` : undefined}>
      {ticks.map(v => (
        <line key={`gx${v}`}
          x1={ox+v*sc} y1={oy-yMin*sc} x2={ox+v*sc} y2={oy-yMax*sc}
          stroke="#f0f0f0" strokeWidth="1"/>
      ))}
      {ticksY.map(v => (
        <line key={`gy${v}`}
          x1={ox+xMin*sc} y1={oy-v*sc} x2={ox+xMax*sc} y2={oy-v*sc}
          stroke="#f0f0f0" strokeWidth="1"/>
      ))}
      <line x1={ox+xMin*sc} y1={oy} x2={ox+xMax*sc} y2={oy} stroke="#ccc" strokeWidth="1.3"/>
      <line x1={ox} y1={oy-yMin*sc} x2={ox} y2={oy-yMax*sc} stroke="#ccc" strokeWidth="1.3"/>
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 1 — Linearity: Additivity and Homogeneity
// Two panels: T(u+v)=Tu+Tv,  T(cu)=cTu
// ─────────────────────────────────────────────────────────────────
function DiagramLinearity() {
  const W = 880, H = 310;
  const sc = 54;

  // Left panel: additivity — show u, v, u+v before and after T
  // T = [[1.2, 0.4],[0.3, 0.9]]
  const T = [1.2, 0.4, 0.3, 0.9];
  const u = [1, 0.5], v = [0.3, 1.2];
  const uv = [u[0]+v[0], u[1]+v[1]];
  const Tu = applyMat(T, u), Tv = applyMat(T, v), Tuv = applyMat(T, uv);

  // Left panel origin
  const Lox = 115, Loy = 185;
  // Right panel origin
  const Rox = 570, Roy = 185;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>The Two Linearity Rules — What Every Linear Transformation Must Satisfy</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <ArrowMarker id="lin_u"  color="#2E75B6"/>
          <ArrowMarker id="lin_v"  color="#27ae60"/>
          <ArrowMarker id="lin_uv" color="#9b59b6"/>
          <ArrowMarker id="lin_Tu"  color="#2E75B6"/>
          <ArrowMarker id="lin_Tv"  color="#27ae60"/>
          <ArrowMarker id="lin_Tuv" color="#9b59b6"/>
          <ArrowMarker id="lin_cu"  color="#2E75B6"/>
          <ArrowMarker id="lin_cTu" color="#e07b00"/>
          <ArrowMarker id="lin_Tcu" color="#c0392b"/>
        </defs>

        {/* ── LEFT: Additivity T(u+v) = T(u)+T(v) ── */}
        <rect x={10} y={8} width={420} height={292} rx={6} fill="#fff" stroke="#2E75B6" strokeWidth="1.6"/>
        <text x={220} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Rule 1 — Additivity:  T(u + v) = T(u) + T(v)
        </text>
        <text x={220} y={42} textAnchor="middle" fontSize="11" fill="#555">
          Adding then transforming = transforming then adding
        </text>

        {/* Input side label */}
        <text x={Lox} y={62} textAnchor="middle" fontSize="11.5" fill="#888">before T</text>
        <Grid ox={Lox} oy={Loy} sc={sc} xRange={[-1.2,2.2]} yRange={[-0.5,2.5]}/>

        {/* u, v, u+v */}
        <Arrow x1={Lox} y1={Loy} x2={Lox+u[0]*sc-3} y2={Loy-u[1]*sc} color="#2E75B6" width={2.5} markerId="lin_u"/>
        <text x={Lox+u[0]*sc+5} y={Loy-u[1]*sc+4} fontSize="13" fill="#2E75B6" fontWeight="700">u</text>

        <Arrow x1={Lox} y1={Loy} x2={Lox+v[0]*sc-2} y2={Loy-v[1]*sc} color="#27ae60" width={2.5} markerId="lin_v"/>
        <text x={Lox+v[0]*sc+5} y={Loy-v[1]*sc} fontSize="13" fill="#27ae60" fontWeight="700">v</text>

        <Arrow x1={Lox} y1={Loy} x2={Lox+uv[0]*sc-3} y2={Loy-uv[1]*sc} color="#9b59b6" width={2.5} markerId="lin_uv" dash="5,3"/>
        <text x={Lox+uv[0]*sc+5} y={Loy-uv[1]*sc+4} fontSize="12" fill="#9b59b6" fontWeight="700">u+v</text>

        {/* T arrow */}
        <text x={225} y={175} fontSize="20" fill="#e07b00">⟹</text>
        <text x={225} y={192} fontSize="11" fill="#e07b00" textAnchor="middle">apply T</text>

        {/* Output side — shifted right */}
        {(() => {
          const ox2 = Lox + 230, oy2 = Loy;
          return (
            <g>
              <text x={ox2} y={62} textAnchor="middle" fontSize="11.5" fill="#888">after T</text>
              <Grid ox={ox2} oy={oy2} sc={sc} xRange={[-1,2.5]} yRange={[-0.5,2.5]}/>
              <Arrow x1={ox2} y1={oy2} x2={ox2+Tu[0]*sc-3} y2={oy2-Tu[1]*sc} color="#2E75B6" width={2.5} markerId="lin_Tu"/>
              <text x={ox2+Tu[0]*sc+5} y={oy2-Tu[1]*sc+4} fontSize="12" fill="#2E75B6" fontWeight="700">T(u)</text>
              <Arrow x1={ox2} y1={oy2} x2={ox2+Tv[0]*sc-2} y2={oy2-Tv[1]*sc} color="#27ae60" width={2.5} markerId="lin_Tv"/>
              <text x={ox2+Tv[0]*sc+5} y={oy2-Tv[1]*sc} fontSize="12" fill="#27ae60" fontWeight="700">T(v)</text>
              <Arrow x1={ox2} y1={oy2} x2={ox2+Tuv[0]*sc-3} y2={oy2-Tuv[1]*sc} color="#9b59b6" width={2.5} markerId="lin_Tuv" dash="5,3"/>
              <text x={ox2+Tuv[0]*sc+5} y={oy2-Tuv[1]*sc+4} fontSize="12" fill="#9b59b6" fontWeight="700">T(u+v)</text>
            </g>
          );
        })()}

        <rect x={18} y={264} width={404} height={28} rx={4} fill="#EBF3FB"/>
        <text x={220} y={282} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">
          T(u+v) lands exactly where T(u)+T(v) lands — same purple arrow
        </text>

        {/* ── RIGHT: Homogeneity T(cu) = cT(u) ── */}
        <rect x={450} y={8} width={420} height={292} rx={6} fill="#fff" stroke="#e07b00" strokeWidth="1.6"/>
        <text x={660} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Rule 2 — Homogeneity:  T(cu) = c·T(u)
        </text>
        <text x={660} y={42} textAnchor="middle" fontSize="11" fill="#555">
          Scaling then transforming = transforming then scaling
        </text>

        {(() => {
          const c = 1.8;
          const cu = [c*u[0], c*u[1]];
          const cTu = [c*Tu[0], c*Tu[1]];
          const Tcu = applyMat(T, cu); // should equal cTu

          return (
            <g>
              <text x={Rox} y={62} textAnchor="middle" fontSize="11.5" fill="#888">before T</text>
              <Grid ox={Rox} oy={Roy} sc={sc} xRange={[-0.5,2.5]} yRange={[-0.5,2.5]}/>

              {/* u */}
              <Arrow x1={Rox} y1={Roy} x2={Rox+u[0]*sc-2} y2={Roy-u[1]*sc} color="#2E75B6" width={2.5} markerId="lin_cu"/>
              <text x={Rox+u[0]*sc+5} y={Roy-u[1]*sc+4} fontSize="13" fill="#2E75B6" fontWeight="700">u</text>

              {/* cu */}
              <Arrow x1={Rox} y1={Roy} x2={Rox+cu[0]*sc-3} y2={Roy-cu[1]*sc} color="#c0392b" width={2.5} markerId="lin_Tcu" dash="5,3"/>
              <text x={Rox+cu[0]*sc+5} y={Roy-cu[1]*sc+4} fontSize="12" fill="#c0392b" fontWeight="700">{`${c}u`}</text>

              {/* T arrow */}
              <text x={660} y={175} fontSize="20" fill="#e07b00">⟹</text>
              <text x={660} y={192} fontSize="11" fill="#e07b00" textAnchor="middle">apply T</text>

              {/* After T — offset right */}
              {(() => {
                const ox2 = Rox + 230, oy2 = Roy;
                return (
                  <g>
                    <text x={ox2} y={62} textAnchor="middle" fontSize="11.5" fill="#888">after T</text>
                    <Grid ox={ox2} oy={oy2} sc={sc} xRange={[-0.5,2.5]} yRange={[-0.5,2.5]}/>
                    <Arrow x1={ox2} y1={oy2} x2={ox2+Tu[0]*sc-2} y2={oy2-Tu[1]*sc} color="#2E75B6" width={2.5} markerId="lin_cTu"/>
                    <text x={ox2+Tu[0]*sc+5} y={oy2-Tu[1]*sc+4} fontSize="12" fill="#2E75B6" fontWeight="700">T(u)</text>
                    <Arrow x1={ox2} y1={oy2} x2={ox2+cTu[0]*sc-3} y2={oy2-cTu[1]*sc} color="#e07b00" width={2.5} markerId="lin_cTu" dash="5,3"/>
                    <text x={ox2+cTu[0]*sc+5} y={oy2-cTu[1]*sc+4} fontSize="12" fill="#e07b00" fontWeight="700">{`${c}·T(u)`}</text>
                  </g>
                );
              })()}

              <rect x={458} y={264} width={404} height={28} rx={4} fill="#fff7ee" stroke="#e07b00" strokeWidth="1"/>
              <text x={660} y={282} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">
                T(1.8u) lands exactly {`${c}×`} as far as T(u) — same direction, scaled length
              </text>
            </g>
          );
        })()}
      </svg>
      <div style={S.dCaption}>
        A transformation is linear if and only if both rules hold simultaneously. Additivity means the origin always maps to the origin and grid lines stay straight. Homogeneity means scaling an input scales the output proportionally. Together they mean: T preserves the structure of linear combinations.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Five Named 2D Transformations
// Each panel: before (light grid) and after (transformed grid + shape)
// ─────────────────────────────────────────────────────────────────
function DiagramNamedTransforms() {
  const W = 880, H = 340;
  const sc = 42;

  // Each transform: matrix [a,b,c,d], title, color, matrix label
  const transforms = [
    {
      title: 'Rotation 45°',
      color: '#2E75B6',
      bg: '#d0e8ff',
      mat: [Math.cos(Math.PI/4), -Math.sin(Math.PI/4), Math.sin(Math.PI/4), Math.cos(Math.PI/4)],
      matLabel: ['cos θ  -sin θ', 'sin θ   cos θ'],
      note: 'det=1, Aᵀ=A⁻¹',
    },
    {
      title: 'Reflection x-axis',
      color: '#27ae60',
      bg: '#d4f0d4',
      mat: [1, 0, 0, -1],
      matLabel: ['1   0', '0  -1'],
      note: 'det=−1, A²=I',
    },
    {
      title: 'Horizontal Shear',
      color: '#9b59b6',
      bg: '#f0e0ff',
      mat: [1, 1, 0, 1],
      matLabel: ['1  k', '0  1'],
      note: 'det=1, area preserved',
    },
    {
      title: 'Uniform Scaling ×1.5',
      color: '#e07b00',
      bg: '#fff3cc',
      mat: [1.5, 0, 0, 1.5],
      matLabel: ['s  0', '0  s'],
      note: 'det=s², isotropic',
    },
    {
      title: 'Projection onto x-axis',
      color: '#c0392b',
      bg: '#ffd0d0',
      mat: [1, 0, 0, 0],
      matLabel: ['1  0', '0  0'],
      note: 'det=0, rank=1',
    },
  ];

  // Reference square corners (input)
  const sq = [[0,0],[1,0],[1,1],[0,1]];

  const panelW = 168;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Five Essential 2D Transformations — Before and After</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        {transforms.map((tr, ti) => {
          const px = ti * (panelW + 6) + 4;
          const ox = px + panelW/2, oy = 185;
          const outSq = sq.map(p => applyMat(tr.mat, p));

          // SVG coords for input square
          const inPoly  = sq.map(p    => [ox+p[0]*sc, oy-p[1]*sc].join(',')).join(' ');
          const outPoly = outSq.map(p => [ox+p[0]*sc, oy-p[1]*sc].join(',')).join(' ');

          const mid = `nt_${ti}`;

          return (
            <g key={ti}>
              {/* Panel */}
              <rect x={px} y={8} width={panelW} height={320} rx={6}
                fill="#fff" stroke={tr.color} strokeWidth="1.8"/>

              {/* Title */}
              <rect x={px} y={8} width={panelW} height={30} rx={6} fill={tr.bg}/>
              <rect x={px} y={28} width={panelW} height={10} fill={tr.bg}/>
              <text x={px+panelW/2} y={26} textAnchor="middle" fontSize="12"
                fill={tr.color} fontWeight="700">{tr.title}</text>

              {/* Grid */}
              {[-1,0,1,2].map(v => (
                <g key={v}>
                  <line x1={px+4} y1={oy-v*sc} x2={px+panelW-4} y2={oy-v*sc} stroke="#f0f0f0" strokeWidth="1"/>
                  <line x1={ox+v*sc} y1={44} x2={ox+v*sc} y2={oy+1.5*sc} stroke="#f0f0f0" strokeWidth="1"/>
                </g>
              ))}
              <line x1={px+4} y1={oy} x2={px+panelW-4} y2={oy} stroke="#ddd" strokeWidth="1.2"/>
              <line x1={ox} y1={44} x2={ox} y2={oy+1.5*sc} stroke="#ddd" strokeWidth="1.2"/>

              {/* Input square — faint */}
              <polygon points={inPoly} fill={tr.bg} opacity="0.35" stroke={tr.color} strokeWidth="1.2" strokeDasharray="4,3"/>

              {/* Output square — solid */}
              <polygon points={outPoly} fill={tr.bg} opacity="0.6" stroke={tr.color} strokeWidth="2.2"/>

              {/* Basis vectors after transform */}
              <defs>
                <marker id={`${mid}_e1`} markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                  <polygon points="0 0,6 3,0 6" fill={tr.color}/>
                </marker>
                <marker id={`${mid}_e2`} markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                  <polygon points="0 0,6 3,0 6" fill="#888"/>
                </marker>
              </defs>

              {/* Ae1 */}
              <line x1={ox} y1={oy}
                x2={ox + outSq[1][0]*sc - (Math.abs(outSq[1][0]) > 0.05 ? Math.sign(outSq[1][0])*3 : 0)}
                y2={oy - outSq[1][1]*sc + (Math.abs(outSq[1][1]) > 0.05 ? Math.sign(outSq[1][1])*3 : 0)}
                stroke={tr.color} strokeWidth="2.2" markerEnd={`url(#${mid}_e1)`}/>

              {/* Ae2 */}
              <line x1={ox} y1={oy}
                x2={ox + outSq[3][0]*sc - (Math.abs(outSq[3][0]) > 0.05 ? Math.sign(outSq[3][0])*3 : 0)}
                y2={oy - outSq[3][1]*sc + (Math.abs(outSq[3][1]) > 0.05 ? Math.sign(outSq[3][1])*3 : 0)}
                stroke="#888" strokeWidth="2" markerEnd={`url(#${mid}_e2)`}/>

              {/* Matrix display */}
              <rect x={px+8} y={220} width={panelW-16} height={52} rx={4}
                fill={tr.bg} opacity="0.5" stroke={tr.color} strokeWidth="1"/>
              {tr.matLabel.map((row, ri) => (
                <text key={ri} x={px+panelW/2} y={238+ri*18}
                  textAnchor="middle" fontSize="11"
                  fill={tr.color} fontFamily="'Courier New',monospace" fontWeight="700">
                  [{row}]
                </text>
              ))}

              {/* Note */}
              <rect x={px+8} y={280} width={panelW-16} height={38} rx={3} fill="#f8fbff" stroke="#ccd9ea" strokeWidth="1"/>
              <text x={px+panelW/2} y={296} textAnchor="middle" fontSize="10.5" fill="#1F3864" fontWeight="700">Properties:</text>
              <text x={px+panelW/2} y={312} textAnchor="middle" fontSize="10.5" fill="#555">{tr.note}</text>
            </g>
          );
        })}
      </svg>
      <div style={S.dCaption}>
        Each transformation is fully defined by where it sends e₁=(1,0) and e₂=(0,1) — those become the columns of the matrix. The dashed square is the original; the solid shape is the output. Notice that rotation preserves shape, shear preserves area (det=1), scaling inflates it, reflection flips orientation (det=−1), and projection collapses one dimension entirely (det=0).
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Kernel and Image
// Two panels: kernel (what collapses to 0) and image (what's reachable)
// ─────────────────────────────────────────────────────────────────
function DiagramKernelImage() {
  const W = 880, H = 300;
  const sc = 48;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Kernel and Image — What a Transformation Collapses and What It Can Reach</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <ArrowMarker id="ki_ker" color="#c0392b"/>
          <ArrowMarker id="ki_img1" color="#2E75B6"/>
          <ArrowMarker id="ki_img2" color="#27ae60"/>
          <ArrowMarker id="ki_zero" color="#888"/>
        </defs>

        {/* ── LEFT: Kernel — projection T collapses y-axis to 0 ── */}
        <rect x={10} y={8} width={415} height={280} rx={6} fill="#fff" stroke="#c0392b" strokeWidth="1.6"/>
        <text x={217} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Kernel (Null Space) — ker(T)
        </text>
        <text x={217} y={42} textAnchor="middle" fontSize="11.5" fill="#555">
          All vectors that T sends to zero
        </text>

        {/* Input space */}
        <text x={115} y={62} textAnchor="middle" fontSize="11" fill="#888">input  ℝ²</text>
        {[-2,-1,0,1,2].map(v => (
          <g key={v}>
            <line x1={18}  y1={165+v*sc} x2={205} y2={165+v*sc} stroke="#f0f0f0" strokeWidth="1"/>
            <line x1={115+v*sc} y1={65} x2={115+v*sc} y2={270} stroke="#f0f0f0" strokeWidth="1"/>
          </g>
        ))}
        <line x1={18}  y1={165} x2={205} y2={165} stroke="#ccc" strokeWidth="1.2"/>
        <line x1={115} y1={65} x2={115} y2={270} stroke="#ccc" strokeWidth="1.2"/>

        {/* Kernel = y-axis highlighted */}
        <rect x={111} y={65} width={8} height={205} fill="#c0392b" opacity="0.18" rx={2}/>
        <line x1={115} y1={65} x2={115} y2={270} stroke="#c0392b" strokeWidth="3" opacity="0.5"/>
        <text x={126} y={80} fontSize="11.5" fill="#c0392b" fontWeight="700">ker(T)</text>
        <text x={126} y={94} fontSize="11" fill="#c0392b">y-axis</text>

        {/* A few kernel vectors */}
        <Arrow x1={115} y1={165} x2={115} y2={165-sc+3} color="#c0392b" width={2.2} markerId="ki_ker"/>
        <Arrow x1={115} y1={165} x2={115} y2={165+sc-3} color="#c0392b" width={2.2} markerId="ki_ker" dash="4,3"/>

        {/* Non-kernel vector */}
        <Arrow x1={115} y1={165} x2={115+1.5*sc-3} y2={165-0.8*sc} color="#888" width={2} markerId="ki_zero"/>
        <text x={115+1.5*sc+4} y={165-0.8*sc+4} fontSize="12" fill="#888">v (not in ker)</text>

        {/* Arrow to output */}
        <text x={222} y={152} fontSize="18" fill="#888">→</text>
        <text x={222} y={168} fontSize="11" fill="#888" textAnchor="middle">T</text>

        {/* Output space — projection onto x-axis */}
        <text x={330} y={62} textAnchor="middle" fontSize="11" fill="#888">output  ℝ¹</text>
        {[-2,-1,0,1,2].map(v => (
          <line key={v} x1={235+v*sc} y1={80} x2={235+v*sc} y2={270} stroke="#f0f0f0" strokeWidth="1"/>
        ))}
        <line x1={228} y1={165} x2={418} y2={165} stroke="#ccc" strokeWidth="1.5"/>

        {/* ker vectors → 0 */}
        <circle cx={330} cy={165} r={6} fill="#c0392b" opacity="0.7" stroke="#fff" strokeWidth="1.5"/>
        <text x={338} y={155} fontSize="12" fill="#c0392b" fontWeight="700">0</text>
        <text x={330} y={185} textAnchor="middle" fontSize="11" fill="#c0392b">all ker vectors → 0</text>

        {/* non-ker vector → some x value */}
        <circle cx={330+1.5*sc} cy={165} r={5} fill="#888" stroke="#fff" strokeWidth="1.5"/>
        <text x={330+1.5*sc} y={185} textAnchor="middle" fontSize="11" fill="#888">T(v) ≠ 0</text>

        <rect x={18} y={248} width={398} height={26} rx={3} fill="#fff0f0" stroke="#c0392b" strokeWidth="1"/>
        <text x={217} y={265} textAnchor="middle" fontSize="11.5" fill="#c0392b" fontWeight="700">
          dim(ker T) = nullity.   ker T = {"{0}"} ↔ T is injective (one-to-one)
        </text>

        {/* ── RIGHT: Image — what can T reach ── */}
        <rect x={440} y={8} width={430} height={280} rx={6} fill="#fff" stroke="#2E75B6" strokeWidth="1.6"/>
        <text x={655} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Image (Column Space) — im(T)
        </text>
        <text x={655} y={42} textAnchor="middle" fontSize="11.5" fill="#555">
          All vectors T can produce — the reachable outputs
        </text>

        {/* Input ℝ² with shaded "all of it" */}
        <text x={540} y={62} textAnchor="middle" fontSize="11" fill="#888">input  ℝ²</text>
        {[-1,0,1,2].map(v => (
          <g key={v}>
            <line x1={448} y1={165+v*sc} x2={620} y2={165+v*sc} stroke="#f0f0f0" strokeWidth="1"/>
            <line x1={540+v*sc} y1={65} x2={540+v*sc} y2={270} stroke="#f0f0f0" strokeWidth="1"/>
          </g>
        ))}
        <line x1={448} y1={165} x2={620} y2={165} stroke="#ccc" strokeWidth="1.2"/>
        <line x1={540} y1={65} x2={540} y2={270} stroke="#ccc" strokeWidth="1.2"/>
        <rect x={450} y={67} width={168} height={200} fill="#EBF3FB" opacity="0.35" rx={2}/>
        <text x={534} y={84} textAnchor="middle" fontSize="11" fill="#2E75B6">all of ℝ²</text>

        {/* Two sample input vectors */}
        <Arrow x1={540} y1={165} x2={540+sc-3} y2={165-0.5*sc} color="#2E75B6" width={2.5} markerId="ki_img1"/>
        <text x={540+sc+4} y={165-0.5*sc+4} fontSize="12" fill="#2E75B6" fontWeight="700">u</text>
        <Arrow x1={540} y1={165} x2={540+0.4*sc} y2={165-1.2*sc} color="#27ae60" width={2.5} markerId="ki_img2"/>
        <text x={540+0.4*sc+5} y={165-1.2*sc} fontSize="12" fill="#27ae60" fontWeight="700">w</text>

        {/* Arrow to output */}
        <text x={636} y={152} fontSize="18" fill="#888">→</text>
        <text x={636} y={168} fontSize="11" fill="#888" textAnchor="middle">T</text>

        {/* Output — shaded image subspace */}
        <text x={760} y={62} textAnchor="middle" fontSize="11" fill="#888">output  ℝ²</text>
        {[-1,0,1,2].map(v => (
          <g key={v}>
            <line x1={652} y1={165+v*sc} x2={862} y2={165+v*sc} stroke="#f0f0f0" strokeWidth="1"/>
            <line x1={760+v*sc} y1={65} x2={760+v*sc} y2={270} stroke="#f0f0f0" strokeWidth="1"/>
          </g>
        ))}
        <line x1={652} y1={165} x2={862} y2={165} stroke="#ccc" strokeWidth="1.2"/>
        <line x1={760} y1={65} x2={760} y2={270} stroke="#ccc" strokeWidth="1.2"/>

        {/* Image = a subspace (line here, for a rank-1 example) */}
        <line x1={660} y1={270} x2={855} y2={68} stroke="#2E75B6" strokeWidth="3" opacity="0.25"/>
        <line x1={660} y1={270} x2={855} y2={68} stroke="#2E75B6" strokeWidth="1.8" strokeDasharray="none"/>
        <text x={855} y={64} fontSize="12" fill="#2E75B6" fontWeight="700">im(T)</text>

        {/* T(u), T(w) land on image */}
        <Arrow x1={760} y1={165} x2={760+sc*0.9} y2={165-sc*1.1} color="#2E75B6" width={2.5} markerId="ki_img1"/>
        <text x={760+sc*0.9+5} y={165-sc*1.1+4} fontSize="12" fill="#2E75B6">T(u)</text>
        <Arrow x1={760} y1={165} x2={760+sc*0.45} y2={165-sc*0.55} color="#27ae60" width={2.5} markerId="ki_img2"/>
        <text x={760+sc*0.45+5} y={165-sc*0.55} fontSize="12" fill="#27ae60">T(w)</text>

        <rect x={448} y={248} width={414} height={26} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
        <text x={655} y={265} textAnchor="middle" fontSize="11.5" fill="#2E75B6" fontWeight="700">
          dim(im T) = rank.   im T = W ↔ T is surjective (onto).   rank + nullity = n
        </text>
      </svg>
      <div style={S.dCaption}>
        The kernel and image together characterise a transformation completely. The kernel tells you what information is lost; the image tells you what's reachable. The Rank-Nullity theorem connects them: their dimensions always add up to the dimension of the input space.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Composition of Transformations
// Show T₂∘T₁ geometrically: rotate then shear
// ─────────────────────────────────────────────────────────────────
function DiagramComposition() {
  const W = 880, H = 300;
  const sc = 50;

  const sq = [[0,0],[1,0],[1,1],[0,1]];
  // T1: rotation 30°
  const c30 = Math.cos(Math.PI/6), s30 = Math.sin(Math.PI/6);
  const T1 = [c30, -s30, s30, c30];
  // T2: horizontal shear k=1
  const T2 = [1, 0.8, 0, 1];
  // T2 ∘ T1: matrix product
  const T2T1 = [
    T2[0]*T1[0]+T2[1]*T1[2], T2[0]*T1[1]+T2[1]*T1[3],
    T2[2]*T1[0]+T2[3]*T1[2], T2[2]*T1[1]+T2[3]*T1[3],
  ];

  const sqT1    = sq.map(p => applyMat(T1, p));
  const sqT2T1  = sq.map(p => applyMat(T2T1, p));

  function polyStr(ox, oy, pts) {
    return pts.map(p => `${ox+p[0]*sc},${oy-p[1]*sc}`).join(' ');
  }

  const panels = [
    { label: 'Original', sq: sq,    color:'#888',   bg:'#f0f0f0', ox:100, oy:185 },
    { label: 'After T₁ (Rotate 30°)', sq: sqT1,   color:'#2E75B6', bg:'#d0e8ff', ox:370, oy:185 },
    { label: 'After T₂∘T₁ (then Shear)', sq: sqT2T1, color:'#9b59b6', bg:'#f0e0ff', ox:660, oy:185 },
  ];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Composition — Chaining Transformations Multiplies Their Matrices</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <ArrowMarker id="comp_e1a" color="#2E75B6"/>
          <ArrowMarker id="comp_e2a" color="#27ae60"/>
          <ArrowMarker id="comp_e1b" color="#9b59b6"/>
          <ArrowMarker id="comp_e2b" color="#c0392b"/>
        </defs>

        {panels.map((p, pi) => {
          const pw = 220, ph = 270;
          const px = p.ox - pw/2;

          return (
            <g key={pi}>
              <rect x={px} y={12} width={pw} height={ph} rx={6}
                fill="#fff" stroke={p.color} strokeWidth={pi===2 ? 2.2 : 1.5}/>
              <text x={p.ox} y={28} textAnchor="middle" fontSize="12"
                fill={p.color} fontWeight="700">{p.label}</text>

              {/* Grid */}
              {[-1,0,1,2].map(v => (
                <g key={v}>
                  <line x1={px+4} y1={p.oy+v*sc} x2={px+pw-4} y2={p.oy+v*sc} stroke="#f0f0f0" strokeWidth="1"/>
                  <line x1={p.ox+v*sc} y1={36} x2={p.ox+v*sc} y2={p.oy+1.2*sc} stroke="#f0f0f0" strokeWidth="1"/>
                </g>
              ))}
              <line x1={px+4} y1={p.oy} x2={px+pw-4} y2={p.oy} stroke="#ccc" strokeWidth="1.2"/>
              <line x1={p.ox} y1={36} x2={p.ox} y2={p.oy+1.2*sc} stroke="#ccc" strokeWidth="1.2"/>

              {/* Shape */}
              <polygon points={polyStr(p.ox, p.oy, p.sq)}
                fill={p.bg} opacity={pi===0 ? 0.4 : 0.55} stroke={p.color} strokeWidth={pi===2 ? 2.2 : 1.8}/>

              {/* Basis vectors for non-trivial panels */}
              {pi > 0 && (
                <g>
                  <line x1={p.ox} y1={p.oy}
                    x2={p.ox+p.sq[1][0]*sc-2} y2={p.oy-p.sq[1][1]*sc}
                    stroke={pi===1 ? '#2E75B6' : '#9b59b6'} strokeWidth="2.2"
                    markerEnd={`url(#${pi===1 ? 'comp_e1a' : 'comp_e1b'})`}/>
                  <line x1={p.ox} y1={p.oy}
                    x2={p.ox+p.sq[3][0]*sc} y2={p.oy-p.sq[3][1]*sc+2}
                    stroke={pi===1 ? '#27ae60' : '#c0392b'} strokeWidth="2.2"
                    markerEnd={`url(#${pi===1 ? 'comp_e2a' : 'comp_e2b'})`}/>
                </g>
              )}

              {/* Arrow to next panel */}
              {pi < 2 && (
                <g>
                  <text x={px+pw+10} y={p.oy-10} fontSize="20" fill="#aaa">→</text>
                  <text x={px+pw+10} y={p.oy+8} fontSize="11" fill={pi===0?'#2E75B6':'#9b59b6'}
                    fontFamily="'Courier New',monospace" fontWeight="700">
                    {pi===0 ? 'T₁' : 'T₂'}
                  </text>
                </g>
              )}
            </g>
          );
        })}

        {/* Bottom equation */}
        <rect x={10} y={256} width={860} height={36} rx={5} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
        <text x={440} y={270} textAnchor="middle" fontSize="12" fill="#1F3864" fontWeight="700">
          T₂∘T₁ applied to v  =  T₂(T₁(v))  =  (T₂T₁)v  — matrix product T₂T₁ does both steps at once
        </text>
        <text x={440} y={285} textAnchor="middle" fontSize="11.5" fill="#2E75B6"
          fontFamily="'Courier New',monospace">
          Order matters:  T₂T₁ ≠ T₁T₂  (rotate-then-shear ≠ shear-then-rotate)
        </text>
      </svg>
      <div style={S.dCaption}>
        Composing two transformations is just matrix multiplication — apply T₁ first (rightmost), then T₂. The combined matrix T₂T₁ achieves the same result in one step. This is why CNC and robotics chains are products of individual joint matrices, always multiplied right to left in the order of application.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Change of Basis
// Show same vector in two coordinate systems
// ─────────────────────────────────────────────────────────────────
function DiagramChangeOfBasis() {
  const W = 880, H = 310;
  const sc = 52;

  // Standard basis e1=(1,0), e2=(0,1)
  // New basis b1=(1,1)/√2, b2=(-1,1)/√2  (rotated 45°)
  // Vector v = (2, 1) in standard coords
  // In new basis: coords are different

  const sqrtH = Math.sqrt(0.5);
  // b1 = (sqrtH, sqrtH), b2 = (-sqrtH, sqrtH)
  // To express v=(2,1) in new basis:
  // [b1|b2]^-1 * v  where B = [[sqrtH,-sqrtH],[sqrtH,sqrtH]]
  // B^-1 = Bᵀ (orthonormal) = [[sqrtH,sqrtH],[-sqrtH,sqrtH]]
  // coords: (sqrtH*2+sqrtH*1, -sqrtH*2+sqrtH*1) = (3sqrtH, -sqrtH) ≈ (2.12, -0.71)

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Change of Basis — Same Vector, Different Coordinate Description</div>
      <svg viewBox={`0 0 ${W} ${H}`} style={{ width:'100%', display:'block' }}>
        <defs>
          <ArrowMarker id="cob_e1"  color="#2E75B6"/>
          <ArrowMarker id="cob_e2"  color="#2E75B6"/>
          <ArrowMarker id="cob_b1"  color="#9b59b6"/>
          <ArrowMarker id="cob_b2"  color="#9b59b6"/>
          <ArrowMarker id="cob_v1"  color="#e07b00"/>
          <ArrowMarker id="cob_v2"  color="#e07b00"/>
          <ArrowMarker id="cob_P"   color="#27ae60"/>
        </defs>

        {/* ── LEFT: Standard basis ── */}
        <rect x={10} y={8} width={380} height={290} rx={6} fill="#fff" stroke="#2E75B6" strokeWidth="1.6"/>
        <text x={200} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Standard Basis  {"{e₁, e₂}"}
        </text>
        <text x={200} y={42} textAnchor="middle" fontSize="11.5" fill="#555">
          v = 2e₁ + 1e₂  →  coordinates (2, 1)
        </text>

        {/* Grid */}
        {[-1,0,1,2,3].map(v => (
          <g key={v}>
            <line x1={18}  y1={185+v*sc} x2={382} y2={185+v*sc} stroke="#f0f0f0" strokeWidth="1"/>
            <line x1={130+v*sc} y1={50}  x2={130+v*sc} y2={290} stroke="#f0f0f0" strokeWidth="1"/>
          </g>
        ))}
        <line x1={18}  y1={185} x2={382} y2={185} stroke="#ccc" strokeWidth="1.3"/>
        <line x1={130} y1={50}  x2={130} y2={290} stroke="#ccc" strokeWidth="1.3"/>

        {/* e1, e2 */}
        <line x1={130} y1={185} x2={130+sc-3} y2={185}
          stroke="#2E75B6" strokeWidth="2.5" markerEnd="url(#cob_e1)"/>
        <text x={130+sc+5} y={189} fontSize="13" fill="#2E75B6" fontWeight="700">e₁</text>
        <line x1={130} y1={185} x2={130} y2={185-sc+3}
          stroke="#2E75B6" strokeWidth="2.5" markerEnd="url(#cob_e2)"/>
        <text x={136} y={185-sc-5} fontSize="13" fill="#2E75B6" fontWeight="700">e₂</text>

        {/* v = (2,1) */}
        <line x1={130} y1={185} x2={130+2*sc-4} y2={185-1*sc}
          stroke="#e07b00" strokeWidth="3" markerEnd="url(#cob_v1)"/>
        <text x={130+2*sc+6} y={185-1*sc+5} fontSize="14" fill="#e07b00" fontWeight="700">v</text>

        {/* Dashed component lines */}
        <line x1={130} y1={185} x2={130+2*sc} y2={185}
          stroke="#e07b00" strokeWidth="1.2" strokeDasharray="4,3" opacity="0.6"/>
        <line x1={130+2*sc} y1={185} x2={130+2*sc} y2={185-sc}
          stroke="#e07b00" strokeWidth="1.2" strokeDasharray="4,3" opacity="0.6"/>
        <text x={130+sc} y={185+16} fontSize="12" fill="#e07b00" textAnchor="middle">2</text>
        <text x={130+2*sc+14} y={185-sc/2} fontSize="12" fill="#e07b00">1</text>

        <rect x={18} y={258} width={364} height={28} rx={3} fill="#EBF3FB"/>
        <text x={200} y={276} textAnchor="middle" fontSize="12" fill="#2E75B6" fontWeight="700">
          Coordinates: [v]ₑ = (2, 1)
        </text>

        {/* ── MIDDLE: change of basis arrow ── */}
        <text x={440} y={158} textAnchor="middle" fontSize="20" fill="#27ae60">⇄</text>
        <text x={440} y={176} textAnchor="middle" fontSize="11" fill="#27ae60" fontWeight="700">P, P⁻¹</text>
        <rect x={400} y={186} width={80} height={52} rx={4} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
        <text x={440} y={202} textAnchor="middle" fontSize="10.5" fill="#27ae60" fontWeight="700">P = [b₁|b₂]</text>
        <text x={440} y={216} textAnchor="middle" fontSize="10" fill="#555">[v]ₑ = P·[v]_B</text>
        <text x={440} y={230} textAnchor="middle" fontSize="10" fill="#555">[v]_B = P⁻¹·[v]ₑ</text>

        {/* ── RIGHT: New (rotated) basis ── */}
        <rect x={490} y={8} width={380} height={290} rx={6} fill="#fff" stroke="#9b59b6" strokeWidth="1.6"/>
        <text x={680} y={26} textAnchor="middle" fontSize="13" fill="#1F3864" fontWeight="700">
          Rotated Basis  {"{b₁, b₂}"}  (45° CCW)
        </text>
        <text x={680} y={42} textAnchor="middle" fontSize="11.5" fill="#555">
          v = 2.12·b₁ − 0.71·b₂  →  different numbers, same arrow
        </text>

        {/* Grid — rotated 45° (drawn as rotated grid lines) */}
        {[-2,-1,0,1,2,3].map(v => (
          <g key={v}>
            {/* Along b1 direction */}
            <line x1={608+v*sc*sqrtH} y1={185+v*sc*sqrtH}
                  x2={608+v*sc*sqrtH+2.5*sc*sqrtH} y2={185+v*sc*sqrtH-2.5*sc*sqrtH}
              stroke="#f0e0ff" strokeWidth="1"/>
            {/* Along b2 direction */}
            <line x1={608+v*sc*sqrtH} y1={185-v*sc*sqrtH}
                  x2={608+v*sc*sqrtH+2.5*sc*sqrtH} y2={185-v*sc*sqrtH+2.5*sc*sqrtH}
              stroke="#f0e0ff" strokeWidth="1"/>
          </g>
        ))}
        {/* Standard grid faint */}
        {[-1,0,1,2,3].map(v => (
          <g key={v}>
            <line x1={498} y1={185+v*sc} x2={862} y2={185+v*sc} stroke="#f8f8f8" strokeWidth="1"/>
            <line x1={608+v*sc} y1={50} x2={608+v*sc} y2={290} stroke="#f8f8f8" strokeWidth="1"/>
          </g>
        ))}
        <line x1={498} y1={185} x2={862} y2={185} stroke="#ddd" strokeWidth="1"/>
        <line x1={608} y1={50} x2={608} y2={290} stroke="#ddd" strokeWidth="1"/>

        {/* b1 = (sqrtH, sqrtH) */}
        <line x1={608} y1={185} x2={608+sc*sqrtH-2} y2={185-sc*sqrtH+2}
          stroke="#9b59b6" strokeWidth="2.5" markerEnd="url(#cob_b1)"/>
        <text x={608+sc*sqrtH+5} y={185-sc*sqrtH+4} fontSize="13" fill="#9b59b6" fontWeight="700">b₁</text>

        {/* b2 = (-sqrtH, sqrtH) */}
        <line x1={608} y1={185} x2={608-sc*sqrtH+2} y2={185-sc*sqrtH+2}
          stroke="#9b59b6" strokeWidth="2.5" markerEnd="url(#cob_b2)"/>
        <text x={608-sc*sqrtH-22} y={185-sc*sqrtH+4} fontSize="13" fill="#9b59b6" fontWeight="700">b₂</text>

        {/* Same v = (2,1) in standard coords */}
        <line x1={608} y1={185} x2={608+2*sc-4} y2={185-1*sc}
          stroke="#e07b00" strokeWidth="3" markerEnd="url(#cob_v2)"/>
        <text x={608+2*sc+6} y={185-1*sc+5} fontSize="14" fill="#e07b00" fontWeight="700">v</text>

        {/* Dashed components along b1, b2 */}
        <line x1={608} y1={185} x2={608+2.12*sc*sqrtH} y2={185-2.12*sc*sqrtH}
          stroke="#9b59b6" strokeWidth="1.2" strokeDasharray="4,3" opacity="0.5"/>
        <line x1={608+2.12*sc*sqrtH} y1={185-2.12*sc*sqrtH}
              x2={608+2*sc} y2={185-1*sc}
          stroke="#9b59b6" strokeWidth="1.2" strokeDasharray="4,3" opacity="0.5"/>

        <rect x={498} y={258} width={364} height={28} rx={3} fill="#f5eeff" stroke="#9b59b6" strokeWidth="1"/>
        <text x={680} y={276} textAnchor="middle" fontSize="12" fill="#9b59b6" fontWeight="700">
          Coordinates: [v]_B = (2.12, −0.71) — same vector, new description
        </text>
      </svg>
      <div style={S.dCaption}>
        The vector v is unchanged — the orange arrow is identical in both panels. What changes is the ruler used to measure it. The change-of-basis matrix P converts coordinate descriptions between systems. This is the core of expressing the same transformation in a more convenient coordinate system — the basis where a matrix is diagonal (its eigenvectors) is usually the most convenient of all.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function LinearTransformations() {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Linear Transformations</h1>

      <p style={S.p}>
        The Matrix Operations page introduced the idea that a matrix acts as a transformation — showing how the unit square deforms under multiplication. This page develops that idea fully. A linear transformation is a function between vector spaces that preserves the structure of linear combinations. Understanding transformations as functions — not just tables of numbers — is what connects matrix algebra to geometry, signal processing, computer graphics, robotics, and structural mechanics.
      </p>

      <InfoBox
        title="Definition:  T: V → W is linear if both hold for all u, v ∈ V and scalars c"
        items={[
          "Additivity:     T(u + v) = T(u) + T(v)   — adding before or after T gives the same result",
          "Homogeneity:    T(cu) = c·T(u)            — scaling before or after T gives the same result",
          "Combined:       T(cu + dv) = cT(u) + dT(v)  — T preserves all linear combinations",
          "Consequence:    T(0) = 0 always — the zero vector must map to the zero vector",
        ]}
      />

      <h2 style={S.h2}>The Two Linearity Rules</h2>
      <DiagramLinearity />
      <Table2 rows={[
        ["Linearity test",         "Check both rules. If either fails, the transformation is not linear.\nT(x) = 2x+1 fails: T(0) = 1 ≠ 0  →  not linear (it's affine)"],
        ["Affine vs linear",       "Affine = linear + translation.  T(x) = Ax + b\nAffine is NOT linear unless b=0. Common confusion in robotics."],
        ["T(0) = 0 always",        "Plug u=v=0 into additivity: T(0+0) = T(0)+T(0) → T(0)=0\nIf the origin doesn't map to origin, T is not linear"],
        ["Linear combo preserved", "T(c₁v₁+...+cₙvₙ) = c₁T(v₁)+...+cₙT(vₙ)\nKnowing T on a basis determines T on everything"],
        ["Matrix representation",  "Every linear T: Rⁿ→Rᵐ is exactly T(v) = Av for some m×n matrix A\nColumns of A = where basis vectors e₁...eₙ land under T"],
        ["Non-examples",           "T(x,y) = (x², y): not linear — scaling fails\nT(x,y) = (x+1, y): not linear — T(0) ≠ 0"],
      ]} />

      <h2 style={S.h2}>The Five Essential 2D Transformations</h2>
      <DiagramNamedTransforms />
      <Table2 rows={[
        ["Rotation by θ",          "[[cosθ, -sinθ],[sinθ, cosθ]]  — det=1, Aᵀ=A⁻¹\nPreserves length and angles. Rotates CCW for positive θ."],
        ["Reflection",             "Across x-axis: [[1,0],[0,-1]].  Across y=x: [[0,1],[1,0]]\ndet=−1 (orientation flips). A²=I (reflecting twice = identity)"],
        ["Shear",                  "Horizontal: [[1,k],[0,1]].  Vertical: [[1,0],[k,1]]\ndet=1 always — shear preserves area. Parallelizes grid lines."],
        ["Scaling",                "Uniform: [[s,0],[0,s]] = sI.  Non-uniform: [[sx,0],[0,sy]]\ndet=s² (uniform) or sx·sy — scales areas by determinant"],
        ["Projection",             "Onto x-axis: [[1,0],[0,0]].  Onto y=x: ½[[1,1],[1,1]]\ndet=0 — collapses one dimension. P²=P (idempotent)"],
        ["Reading the matrix",     "Columns of A = images of e₁ and e₂ under T\nAlways build the matrix by asking: where does each basis vector go?"],
      ]} />

      <h2 style={S.h2}>Kernel and Image</h2>
      <DiagramKernelImage />
      <Table2 rows={[
        ["Kernel  ker(T)",         "ker(T) = {v : T(v) = 0} — all inputs that collapse to zero\nSame as the null space of the matrix A representing T"],
        ["Image  im(T)",           "im(T) = {T(v) : v ∈ V} — all reachable outputs\nSame as the column space of A.  im(T) is always a subspace of W"],
        ["Rank-Nullity theorem",   "dim(ker T) + dim(im T) = dim(V)\nnullity + rank = n — the two always balance"],
        ["Injective (one-to-one)", "ker(T) = {0} — different inputs always give different outputs\nEquivalent to: A has full column rank, no free variables"],
        ["Surjective (onto)",      "im(T) = W — every output is reachable\nEquivalent to: A has full row rank, column space = Rᵐ"],
        ["Bijective (invertible)", "Both injective and surjective: ker={0} and im=W\nEquivalent to: A is square and det≠0 — T⁻¹ exists"],
      ]} />

      <h2 style={S.h2}>Composition of Transformations</h2>
      <DiagramComposition />
      <Table2 rows={[
        ["Composition T₂∘T₁",     "Apply T₁ first, then T₂.  Matrix: multiply A₂A₁ (right to left)\n(T₂∘T₁)(v) = T₂(T₁(v)) = A₂(A₁v) = (A₂A₁)v"],
        ["Order matters",         "A₂A₁ ≠ A₁A₂ in general — rotate-then-shear ≠ shear-then-rotate\nThis is why matrix multiplication is non-commutative"],
        ["Robotics chain",        "End-effector pose = T_base × T_joint1 × T_joint2 × ... × T_tool\nEach joint adds one transformation on the right"],
        ["CNC / CAD transforms",  "World→part→feature coordinate chain = product of transform matrices\nSolidWorks, CATIA, Fusion 360 all compose transforms this way"],
        ["Inverse of composition","(T₂∘T₁)⁻¹ = T₁⁻¹ ∘ T₂⁻¹  — reverse order, invert each\nSame reversal rule as (AB)⁻¹ = B⁻¹A⁻¹"],
        ["Identity transform",    "T(v) = v for all v — matrix = I\nComposing any T with identity: T∘I = I∘T = T"],
      ]} />

      <h2 style={S.h2}>Change of Basis</h2>
      <DiagramChangeOfBasis />
      <Table2 rows={[
        ["Change-of-basis matrix P","P = [b₁ | b₂ | ... | bₙ] — columns are the new basis vectors\nExpresses new basis vectors in terms of the old (standard) basis"],
        ["Converting coordinates", "[v]_standard = P · [v]_B\n[v]_B = P⁻¹ · [v]_standard"],
        ["Same transformation, new basis","[T]_B = P⁻¹ · [T]_standard · P  (similarity transform)\nSame linear map, different matrix depending on basis choice"],
        ["Why change basis?",      "In the eigenvector basis, a matrix becomes diagonal — easy to work with\nFourier transform = change to frequency basis; PCA = change to variance basis"],
        ["Orthonormal basis",      "If new basis is orthonormal: P⁻¹ = Pᵀ — very fast to compute\nRotation matrices are exactly change-of-basis to a rotated orthonormal frame"],
        ["Diagonal form",          "If A = PDP⁻¹ where D is diagonal, then the columns of P are eigenvectors\nAⁿ = PDⁿP⁻¹ — raising to powers is trivial in the eigenbasis"],
      ]} />

      <InfoBox
        title="Key Takeaway — The Three Things Every Transformation Page Connects"
        items={[
          "Function view: T is a linear map — it preserves combinations. Check T(0)=0 and both rules.",
          "Matrix view: every linear T: Rⁿ→Rᵐ is T(v)=Av. Build A by asking where each basis vector lands.",
          "Geometric view: rotation (det=1), reflection (det=−1), shear (det=1), scaling (det=s²), projection (det=0).",
          "Kernel = what's lost (null space). Image = what's reachable (column space). They balance: rank + nullity = n.",
          "Composition = matrix multiplication, right to left. Change of basis = P⁻¹AP. Eigenvector basis = diagonal form.",
        ]}
      />
    </div>
  );
}